import { HttpErrorResponse } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import {
  VUE_EMAIL_REGEX, VueForgotCredentialsService,
  VueFormError,
  VueServerMessage, VueServerMessageType, VueTranslationService
} from '@vue/vueframework';
import { AccountManagementService } from '../shared/services/account-management.service';
import { VueUserNameLookup } from '@vue/vueframework/lib/models/vue-account-username-lookup.interface';
import { AppDefaultsService } from '../app-defaults.service';

/**
 * This component is to initiate and then display person username if they forget it.
 */
/** @dynamic */
@Component({
  selector: 'username-lookup',
  styleUrls: [ './username-lookup.component.scss' ],
  templateUrl: './username-lookup.component.html',
  standalone: false
})
export class UsernameLookupComponent {
  /**
   * Form for to verify user information
   */
  @ViewChild('verifyUserForm') public verifyUserForm: NgForm;

  /**
   * Standard vue form error
   */
  public formError = new VueFormError();

  /**
   * Object to hold to verify details
   */
  public verifyDetails: VueUserNameLookup = {
    email: null,
    firstName: null,
    lastName: null,
    siteID: null,
    siteIDRequired: null
  };

  /**
   * Page level messages
   */
  public messages: VueServerMessage[] = [];

  /**
   * Regex email validation pattern
   */
  public emailRegex = VUE_EMAIL_REGEX;

  public constructor(
    private router: Router,
    private accountManagementService: AccountManagementService,
    private translationService: VueTranslationService,
    private defaultService: AppDefaultsService,
    private forgotCreds: VueForgotCredentialsService) {
    this.verifyDetails.siteIDRequired = this.defaultService.getLoginSiteIDRequired();
  }

  /**
   * Call the api to get username details.
   */
  public getUserDetails(): void {
    this.formError.invalidFormSubmit = false;
    this.messages = [];
    if (this.verifyUserForm.form.valid) {
      this.accountManagementService.userlookup(this.verifyDetails).subscribe(
        {
          error: (err) => this.apiFailure(err),
          next: (username) => this.getUserSuccess(username)
        }
      );
    } else {
      this.formError.invalidFormSubmit = true;
    }
  }

  /**
   * Called after the users successfully submits valid info
   *
   * @param username string
   */
  public getUserSuccess(username: string): void {
    const message = this.translationService.getTranslation(
      'Connect',
      'info.usernameLookup',
      [ username ]
    );
    this.router.navigate([ '/authenticate' ], {
      state: {
        message: new VueServerMessage(VueServerMessageType.Success, message, true),
        username: username
      }
    });
  }

  /**
   * Runs when username cannot be found or another error occurs
   * @param response HttpErrorResponse
   */
  public apiFailure(response: HttpErrorResponse): void {
    const code = response.error[ 0 ];
    const errorMessage = this.translationService.getTranslationFromServerCode(
      code
    );

    this.messages = [ new VueServerMessage({
      messageText: errorMessage,
      scrollIntoView: true,
      type: VueServerMessageType.Error
    }) ];
  }

  /**
   * Return to login
   */
  public backToLogin(): void {
    this.router.navigate([ '/authenticate' ]);
  }

  /**
   * Navigate to forgot password
   */
  public forgotPassword(): void {
    this.forgotCreds.forgotPassword();
  }
}
